"use client";
import React, { PropsWithChildren, useRef, useState } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { CompanyLogoCard } from "@/components/ui/cards/companyLogoCard";
import {
  RotatingText,
  type RotatingTextRef,
} from "@/components/animations/RotatingText";
import Link from "next/link";
import { ButtonConfig } from "@/types";
import { Heading } from "../ui/Heading";

const companyTeaserVariants = cva("container", {
  variants: {
    variant: {
      light: "text-white",
      dark: "text-black",
    },
  },
  defaultVariants: {
    variant: "light",
  },
});

const sublineVariants = cva("headline-2", {
  variants: {
    variant: {
      light: "text-white",
      dark: "text-black",
    },
  },
  defaultVariants: {
    variant: "light",
  },
});

const rotatingTextVariants = cva("ml-2", {
  variants: {
    variant: {
      light: "text-yellow",
      dark: "text-purple",
    },
  },
  defaultVariants: {
    variant: "light",
  },
});

export interface CompanyLogo {
  /**
   * Name of the company
   */
  companyName: string;

  /**
   * Raw SVG string for the company logo
   */
  svg: string;

  button?: ButtonConfig;
}

export interface CompanyTeaserProps extends VariantProps<
  typeof companyTeaserVariants
> {
  /**
   * Main headline text
   */
  headline?: string;

  /**
   * Subheadline text
   */
  subline?: string;

  /**
   * Array of company logos to display
   */
  companies: CompanyLogo[];

  /**
   * Controls whether a rotating company name is shown above the component.
   */
  showCompanyName?: boolean;

  /**
   * Optional className for custom styling
   */
  className?: string;
}

/**
 * CompanyTeaser component - displays company logos in a grid with rotating headline
 * Used to showcase partnerships or group affiliations
 * Features rotating text that updates on card hover
 */
export const CompanyTeaser: React.FC<CompanyTeaserProps> = ({
  headline,
  subline,
  companies,
  variant = "light",
  className = "",
  showCompanyName = true,
}) => {
  const rotatingTextRef = useRef<RotatingTextRef>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleCardHover = (index: number) => {
    setCurrentIndex(index);
    rotatingTextRef.current?.jumpTo(index);
  };

  const companyNames = companies.map((company) => company.companyName);

  return (
    <div className={companyTeaserVariants({ variant, className })}>
      <div className="col-span-9">
        {(showCompanyName || headline) && (
          <Heading style="h2" className="flex">
            {headline}{" "}
            {showCompanyName && (
              <RotatingText
                ref={rotatingTextRef}
                texts={companyNames}
                splitBy={"word"}
                auto={false}
                staggerDuration={0.01}
                rotationInterval={1000}
                transition={{ type: "spring", damping: 30, stiffness: 500 }}
                className={rotatingTextVariants({ variant })}
              />
            )}
          </Heading>
        )}
        <p className={sublineVariants({ variant })}>{subline}</p>
      </div>
      <div className="col-span-full grid md:grid-cols-5 grid-cols-2 gap-sm">
        {companies.map((company, index) => (
          <CompanyWrapper
            key={`${company.companyName}-${index}`}
            button={company.button}
            onMouseEnter={() => handleCardHover(index)}
          >
            <CompanyLogoCard
              companyName={company.companyName}
              svg={company.svg}
              variant={variant}
              isActive={currentIndex === index}
            />
          </CompanyWrapper>
        ))}
      </div>
    </div>
  );
};

interface CompanyWrapperProps
  extends Pick<CompanyLogo, "button">, PropsWithChildren {
  onMouseEnter: () => void;
}

const CompanyWrapper: React.FC<CompanyWrapperProps> = ({
  button,
  children,
  onMouseEnter,
}) => {
  if (button?.link?.href && button.link.href.length > 0) {
    return (
      <Link
        href={button.link.href}
        aria-label={button.link.label}
        target="_blank"
        referrerPolicy="no-referrer"
        onMouseEnter={onMouseEnter}
      >
        {children}
      </Link>
    );
  }
  return <div onMouseEnter={onMouseEnter}>{children}</div>;
};
